import React, { useState, useEffect } from 'react'
import { Trash2, Edit2 } from 'lucide-react'
import Button from '@/components/ui/Button'
import SidePanel from '@/components/ui/SidePanel'
import DeleteConfirmModal from '@/components/ui/DeleteConfirmModal'
import { designTokens } from '@/utils/design-tokens'
import type { ArticleResponse } from '@/api/generated/models/ArticleResponse'
import { ArticleService } from '@/api/generated/services/ArticleService'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError, ErrorMessages } from '@/utils/api-error-handler'

interface MeetingDetailPanelProps {
  isOpen: boolean
  onClose: () => void
  /** 표시할 회의록 ID. null이면 패널 내용 비움 */
  meetingId: string | null
  /** 부모가 수정/삭제 성공 후 이 값을 증가시키면 패널이 자체적으로 재조회 */
  refreshKey?: number
  /** 삭제 성공 후 부모 리스트 새로고침용 */
  onSuccess?: () => void
  /** 수정 버튼 클릭 시: 패널이 내부 로드한 ArticleResponse를 부모에 전달 */
  onEdit?: (meeting: ArticleResponse) => void
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div
    className="pb-2 border-b"
    style={{
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Info Field Component
const InfoField: React.FC<{
  label: string
  value: string | React.ReactNode
  fullWidth?: boolean
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const MeetingDetailPanel: React.FC<MeetingDetailPanelProps> = ({
  isOpen,
  onClose,
  meetingId,
  refreshKey,
  onSuccess,
  onEdit,
}) => {
  const { showError } = useAlert()

  // ===== 자체 데이터 로드 =====
  const [loadedMeeting, setLoadedMeeting] = useState<ArticleResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // 패널이 닫힐 때 모달 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setIsDeleteModalOpen(false)
      setIsDeleting(false)
      setDeleteError(null)
      setLoadError(null)
    }
  }, [isOpen])

  // 회의록 단건 fetch
  useEffect(() => {
    if (!isOpen || !meetingId) {
      setLoadedMeeting(null)
      return
    }
    let cancelled = false
    setIsLoading(true)
    setLoadError(null)
    ArticleService.appsArticleViewsGetArticle({ articleId: meetingId })
      .then(res => {
        if (!cancelled) setLoadedMeeting(res)
      })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load meeting detail:', err)
        handleApiError(err, showError, ErrorMessages.FETCH_FAILED)
        setLoadError('회의록을 불러오지 못했습니다.')
        setLoadedMeeting(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, meetingId, refreshKey, showError])

  // 패널이 닫혀있으면 아무것도 렌더하지 않음
  if (!isOpen) return null

  // 로딩/에러/빈 상태 → 패널은 열린 채로 placeholder
  if (!loadedMeeting) {
    return (
      <SidePanel
        isOpen={isOpen}
        onClose={onClose}
        title="회의록 상세"
        width={800}
      >
        <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
          {loadError ? (
            <span style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.error.DEFAULT,
            }}>
              {loadError}
            </span>
          ) : isLoading ? (
            <div className="flex flex-col items-center gap-3">
              <div
                className="animate-spin rounded-full h-8 w-8 border-b-2"
                style={{ borderColor: designTokens.colors.primary.DEFAULT }}
              />
              <span style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '14px',
                color: designTokens.colors.gray[5],
              }}>
                데이터를 불러오는 중...
              </span>
            </div>
          ) : (
            <span style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.gray[4],
            }}>
              표시할 항목이 없습니다.
            </span>
          )}
        </div>
      </SidePanel>
    )
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const headerActions = (
    <>
      <Button
        size="small"
        color="dark"
        onClick={() => setIsDeleteModalOpen(true)}
        className="flex items-center gap-1"
      >
        <Trash2 className="w-4 h-4" />
        삭제
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={() => onEdit?.(loadedMeeting)}
        className="flex items-center gap-1"
      >
        <Edit2 className="w-4 h-4" />
        수정
      </Button>
      {/*<Button*/}
      {/*  size="small"*/}
      {/*  color="dark"*/}
      {/*  onClick={() => console.log('More', loadedMeeting.id)}*/}
      {/*  className="flex items-center gap-1"*/}
      {/*>*/}
      {/*  <MoreHorizontal className="w-4 h-4" />*/}
      {/*  더보기*/}
      {/*</Button>*/}
    </>
  )

  const handleDeleteConfirm = async () => {
    if (!loadedMeeting) return

    setIsDeleting(true)
    setDeleteError(null)

    try {
      await ArticleService.appsArticleViewsDeleteArticle({
        articleId: loadedMeeting.id
      })

      setIsDeleteModalOpen(false)
      onClose()
      onSuccess?.()
    } catch (err) {
      console.error('Failed to delete meeting note:', err)

      if (err && typeof err === 'object' && 'status' in err) {
        switch (err.status) {
          case 401:
            setDeleteError('인증이 만료되었습니다. 다시 로그인해주세요.')
            break
          case 404:
            setDeleteError('삭제하려는 회의록을 찾을 수 없습니다.')
            break
          case 500:
            setDeleteError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
            break
          default:
            setDeleteError('회의록 삭제에 실패했습니다. 다시 시도해주세요.')
        }
      } else {
        setDeleteError('회의록 삭제에 실패했습니다. 다시 시도해주세요.')
      }
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <SidePanel
        isOpen={isOpen}
        onClose={onClose}
        title="회의록 상세"
        width={800}
        headerActions={headerActions}
      >
        <div
          className="flex flex-col space-y-2"
          style={{
            padding: '24px',
          }}
        >
          {/* Section 1: 기본정보 */}
          <div className="space-y-2">
            <SectionHeader title="기본정보" />
            <div className="space-y-2" style={{ padding: '0px 4px' }}>
              <InfoField
                label="워크스페이스"
                value={loadedMeeting.workspace_name || '-'}
              />
              <InfoField
                label="생성자"
                value={loadedMeeting.creator_name || '-'}
              />
              <InfoField
                label="담당자"
                value={loadedMeeting.assignee_name || '-'}
              />
              <InfoField
                label="생성일시"
                value={loadedMeeting.created_at ? formatDate(loadedMeeting.created_at) : '-'}
              />
              <InfoField
                label="수정일시"
                value={loadedMeeting.updated_at ? formatDate(loadedMeeting.updated_at) : '-'}
              />
            </div>
          </div>

          {/* Section 2: 회의록 내용 */}
          <div className="space-y-2">
            <SectionHeader title="회의록 내용" />
            <div className="space-y-2" style={{ padding: '0px 4px' }}>
              <InfoField
                label="제목"
                value={loadedMeeting.title || '-'}
                fullWidth
              />
              <InfoField
                label="내용"
                value={
                  <div
                    className="whitespace-pre-wrap"
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6],
                      lineHeight: '1.5',
                      minHeight: '200px',
                      padding: '12px',
                      border: `1px solid ${designTokens.colors.gray[1]}`,
                      borderRadius: '4px',
                      backgroundColor: designTokens.colors.gray[0]
                    }}
                  >
                    {loadedMeeting.content || '내용이 없습니다.'}
                  </div>
                }
                fullWidth
              />
            </div>
          </div>
        </div>
      </SidePanel>

      {/* Delete Confirmation Modal — DeleteConfirmModal은 error prop이 없어 message에 합성 */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false)
          setDeleteError(null)
        }}
        onConfirm={handleDeleteConfirm}
        title="회의록 삭제"
        message={deleteError ?? '회의록을 삭제하시겠습니까?'}
        isDeleting={isDeleting}
      />
    </>
  )
}

export default MeetingDetailPanel